import React, { useMemo, useState } from 'react';
import { Box, styled, Typography, Stack, Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Dropzone, { FileRejection, FileWithPath } from 'react-dropzone';
import PromiseThrottle from 'promise-throttle';
import DataGridTable from '@/components/datagridTable/datagridTable';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { nanoid } from 'nanoid';
import BlinkingText from '@/components/blinkingText';
import ErrorMessage from '@/components/errorMessage';

const DropzoneContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: 'dashed',
  borderColor: theme.palette.primary.main,
  borderRadius: 10,
  boxSizing: 'border-box',
  cursor: 'pointer',
  overflow: 'hidden',
}));

const DropzoneText = styled(Box)(({ theme }) => ({ margin: 12, textAlign: 'center' }));

const DEFAULT_MEDIA_TYPES = ['PDF', 'DOCX', 'DOC', 'TEXT', 'HTML'];

const EXCEL_CSV_ACCEPT_FILE_TYPES: Record<string, string[]> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
  'text/plain': ['.txt', '.csv'],
};

const FILE_TYPE_MAPPINGS: Record<string, Record<string, string[]>> = {
  pdf: {
    'application/pdf': ['.pdf'],
  },
  docx: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/x-tika-ooxml': ['.docx'],
  },
  doc: {
    'application/msword': ['.doc'],
    'application/x-tika-msoffice': ['.doc'],
  },
  text: {
    'text/plain': ['.txt', '.text'],
  },
  txt: {
    'text/plain': ['.txt', '.text'],
  },
  html: {
    'text/html': ['.html', '.htm'],
  },
  htm: {
    'text/html': ['.html', '.htm'],
  },
  json: {
    'application/json': ['.json'],
  },
  csv: {
    'text/csv': ['.csv'],
    'application/csv': ['.csv'],
  },
  xml: {
    'application/xml': ['.xml'],
    'text/xml': ['.xml'],
  },
  zip: {
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
  },
  xlsx: {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  xls: {
    'application/vnd.ms-excel': ['.xls'],
  },
  pptx: {
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  },
  ppt: {
    'application/vnd.ms-powerpoint': ['.ppt'],
  },
  officedocuments: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  image: {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/jpg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
    'image/tiff': ['.tiff', '.tif'],
    'image/bmp': ['.bmp'],
    'image/x-ms-bmp': ['.bmp'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
  },
  audio: {
    'audio/mpeg': ['.mp3'],
    'audio/mp3': ['.mp3'],
    'audio/mp4': ['.m4a'],
    'audio/aac': ['.aac'],
    'audio/wav': ['.wav'],
    'audio/x-wav': ['.wav'],
    'audio/ogg': ['.ogg'],
    'audio/flac': ['.flac'],
    'audio/x-flac': ['.flac'],
    'audio/webm': ['.weba'],
  },
  video: {
    'video/mp4': ['.mp4'],
    'video/quicktime': ['.mov'],
    'video/x-msvideo': ['.avi'],
    'video/webm': ['.webm'],
    'video/x-matroska': ['.mkv'],
    'video/mpeg': ['.mpeg', '.mpg'],
    'video/3gpp': ['.3gp'],
    'video/3gpp2': ['.3g2'],
  },
  document: {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/x-tika-ooxml': ['.docx'],
    'application/msword': ['.doc'],
    'application/x-tika-msoffice': ['.doc'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt', '.text'],
    'text/html': ['.html', '.htm'],
    'application/json': ['.json'],
    'application/xml': ['.xml'],
    'text/csv': ['.csv'],
  },
};

const buildAcceptFileTypes = (types: string[]): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  const addMapping = (mime: string, exts: string[]) => {
    if (!result[mime]) {
      result[mime] = [];
    }
    exts.forEach((ext) => {
      const formatted = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
      if (!result[mime].includes(formatted)) {
        result[mime].push(formatted);
      }
    });
  };

  types.forEach((type) => {
    if (!type) return;
    const cleanType = String(type).trim();
    const key = cleanType.toLowerCase().replace(/[\s-_]+/g, '');

    if (FILE_TYPE_MAPPINGS[key]) {
      Object.entries(FILE_TYPE_MAPPINGS[key]).forEach(([mime, exts]) => {
        addMapping(mime, exts);
      });
    } else if (cleanType.includes('/')) {
      const sub = cleanType.split('/')[1]?.replace('*', '');
      const ext = sub ? `.${sub}` : '';
      addMapping(cleanType, ext ? [ext] : []);
    } else if (cleanType.startsWith('.')) {
      addMapping('application/octet-stream', [cleanType]);
    } else {
      addMapping('application/octet-stream', [`.${cleanType}`]);
    }
  });

  return result;
};

type Props = { dataSourceInstance: any; header?: string; onCancel: () => Promise<void> };

const DataSourceItemUpload = ({ dataSourceInstance, header = 'Upload files', onCancel }: Props) => {
  const apiContext = useApiContext();
  const { toast } = useNotificationContext();

  const [uploading, setUploading] = useState(false);

  const maxFiles = parseInt(import.meta.env.VITE_MAX_FILE_UPLOAD);
  const maxFileSize = parseInt(import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE);
  const maxConcUpload = parseInt(import.meta.env.VITE_MAX_CONCURRENT_UPLOAD);

  const promiseThrottle = new PromiseThrottle({ requestsPerSecond: maxConcUpload, promiseImplementation: Promise });

  const defId =
    dataSourceInstance?.dataSourceDefId ||
    dataSourceInstance?.entityDefId ||
    dataSourceInstance?.dataSourceDef?.id;
  const isExcelCsv = defId === 'excelCsv';

  const acceptFileTypes = useMemo(() => {
    if (isExcelCsv) {
      return EXCEL_CSV_ACCEPT_FILE_TYPES;
    }

    const rawMediaTypes = dataSourceInstance?.settings?.DOCUMENT_FILTER_INCLUDE_MEDIA_TYPES;
    let selectedTypes: string[] = [];
    if (Array.isArray(rawMediaTypes) && rawMediaTypes.length > 0) {
      selectedTypes = rawMediaTypes;
    } else if (typeof rawMediaTypes === 'string' && rawMediaTypes.trim()) {
      selectedTypes = rawMediaTypes.split(',').map((s: string) => s.trim()).filter(Boolean);
    } else {
      selectedTypes = DEFAULT_MEDIA_TYPES;
    }

    const mapped = buildAcceptFileTypes(selectedTypes);
    return Object.keys(mapped).length > 0 ? mapped : buildAcceptFileTypes(DEFAULT_MEDIA_TYPES);
  }, [isExcelCsv, dataSourceInstance?.settings?.DOCUMENT_FILTER_INCLUDE_MEDIA_TYPES]);

  const allowedExtensionsText = useMemo(() => {
    const exts = new Set<string>();
    Object.values(acceptFileTypes).forEach((extList) => {
      extList.forEach((ext) => {
        exts.add(ext.replace(/^\./, '').toLowerCase());
      });
    });
    return Array.from(exts).join(', ');
  }, [acceptFileTypes]);

  const [filesToUpload, setFilesToUpload] = useState<any[]>([]);
  const [fileErrors, setFileErrors] = useState<any[]>([]);
  const [uploadStatus, setUploadStatus] = useState({} as any);
  const [allUploaded, setAllUploaded] = useState(false);

  const columns: GridColDef[] = [
    {
      field: 'filename',
      headerName: 'File name',
      flex: 1,
      valueGetter: (value: any, row: any) => {
        return row.file.name;
      },
    },
    {
      field: 'filesize',
      headerName: 'File size',
      width: 80,
      valueGetter: (value: any, row: any) => {
        return `${row.file.size}`;
      },
    },
    {
      field: 'status',
      headerName: '',
      width: 150,
      renderCell: (params) => {
        const status = uploadStatus[params.id] || {};
        return status.uploading ? (
          <BlinkingText>'Uploading'</BlinkingText>
        ) : status.uploadingComplete ? (
          'Uploaded'
        ) : status.error ? (
          status.error.message
        ) : (
          ''
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params: any) =>
        !(uploadStatus[params.id]?.uploading || uploadStatus[params.id]?.uploadingComplete)
          ? [<GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={deleteFile(params)} />]
          : [],
    },
  ];

  const onDrop = (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
    const errors = [];
    if (filesToUpload.length + acceptedFiles.length > maxFiles) {
      errors.push(`Too many files selected. You can upload max ${maxFiles} files only.`);
      setFileErrors(errors);
      return;
    }
    const files = acceptedFiles
      .filter(
        (f) =>
          filesToUpload.find((f1) => {
            const p1 = f1.file.path;
            const p2 = f.path || '';
            const n1 = f1.file.name;
            const n2 = f.name;
            return p1.toLowerCase() === p2.toLowerCase() && n1.toLowerCase() === n2.toLowerCase();
          }) === undefined,
      )
      .map((file, idx) => {
        return { id: nanoid(), file, status: '' };
      });
    setFilesToUpload((existing) => [...existing, ...files]);
    const tooManyFiles = fileRejections.filter((fr) => {
      const errorCodes = fr.errors.map((e) => e.code);
      return errorCodes.includes('too-many-files');
    });
    const fileTooLarge = fileRejections.filter((fr) => {
      const errorCodes = fr.errors.map((e) => e.code);
      return errorCodes.includes('file-too-large');
    });
    const invalidTypes = fileRejections.filter((fr) => {
      const errorCodes = fr.errors.map((e) => e.code);
      return errorCodes.includes('file-invalid-type');
    });
    const otherRejections = fileRejections.filter((fr) => {
      const errorCodes = fr.errors.map((e) => e.code);
      return (
        !errorCodes.includes('too-many-files') &&
        !errorCodes.includes('file-too-large') &&
        !errorCodes.includes('file-invalid-type')
      );
    });

    if (tooManyFiles.length > 0) {
      errors.push(`Too many files selected. You can upload max ${maxFiles} files only.`);
    }
    fileTooLarge.forEach((fr) => {
      errors.push(`${fr.file.name}: ${fr.errors.map((e) => e.message).join()}`);
    });
    invalidTypes.forEach((fr) => {
      errors.push(
        `${fr.file.name}: File type is not allowed. Allowed types: ${allowedExtensionsText}`,
      );
    });
    otherRejections.forEach((fr) => {
      errors.push(`${fr.file.name}: ${fr.errors.map((e) => e.message).join(', ')}`);
    });
    setFileErrors(errors);
    setAllUploaded(false);
  };

  const deleteFile = React.useCallback(
    (file: any) => () => {
      setTimeout(() => {
        setFilesToUpload((prevValues) => prevValues.filter((f) => f.id !== file.id));
        setUploadStatus((prevValues: any) => ({ ...prevValues, [file.id]: null }));
      });
    },
    [],
  );

  const submitFiles = async () => {
    setUploading(true);
    const promises = [];
    for (const f of filesToUpload) {
      if (!(uploadStatus[f.id]?.uploading || uploadStatus[f.id]?.uploadingComplete)) {
        promises.push(promiseThrottle.add(performUpload.bind(this, f)));
      }
    }
    await Promise.all(promises).then(function (r) {
      setUploading(false);
      toast({ message: 'Upload complete' });
      setAllUploaded(true);
    });
  };

  const performUpload = async (file: any) => {
    try {
      setUploadStatus((prevValue: any) => ({ ...prevValue, [file.id]: { uploading: true, error: false } }));
      await apiContext.uploadDataSourceInstanceItem(
        dataSourceInstance.dataSourceDefId,
        dataSourceInstance.id,
        file.file,
      );
      setUploadStatus((prevValue: any) => ({ ...prevValue, [file.id]: { uploading: false, uploadingComplete: true } }));
    } catch (error) {
      setUploadStatus((prevValue: any) => ({
        ...prevValue,
        [file.id]: { uploading: false, uploadingComplete: false, error },
      }));
    }
  };

  const close = async () => {
    if (filesToUpload.length > 0) {
      try {
        setUploading(true);
        // await runDataSourceIngestionPipeline(dataSource.id, false);
      } catch (error) {
        console.log(error);
      }
      setUploading(false);
    }
    onCancel();
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {header}
        </Typography>
      </Box>

      {/* Body */}
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0, p: 2.5, overflowY: 'auto' }}>
        <Dropzone onDrop={onDrop} maxFiles={maxFiles} multiple maxSize={maxFileSize} accept={acceptFileTypes}>
          {({ getRootProps, getInputProps }) => (
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <DropzoneText>
                <Typography>
                  <CloudUploadIcon />
                </Typography>
                <Typography>Drag 'n' drop some files here, or click to select files</Typography>
                {allowedExtensionsText && <Typography>({allowedExtensionsText})</Typography>}
                <Typography>
                  (Maximum 10 files, Maximum file size {Math.round(maxFileSize / (1024 * 1024))}mb)
                </Typography>
              </DropzoneText>
            </DropzoneContainer>
          )}
        </Dropzone>
        <Box sx={{ overflowY: 'auto' }}>
          {filesToUpload.length > 0 && <DataGridTable rows={filesToUpload} columns={columns} />}
          {fileErrors.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {fileErrors.map((fe, idx) => (
                <ErrorMessage key={idx} error={fe} />
              ))}
            </Box>
          )}
        </Box>
      </Stack>

      {/* Action bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button
          loading={uploading}
          size="small"
          variant="text"
          onClick={close}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {allUploaded ? 'Close' : 'Cancel'}
        </Button>
        <Button
          loading={uploading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={submitFiles}
          disabled={allUploaded}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Upload
        </Button>
      </Box>
    </Box>
  );
};

export default DataSourceItemUpload;
